import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { useDropzone } from 'react-dropzone';
import { Upload, File, CheckCircle, AlertCircle, Loader2, Sparkles, Languages, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Circular progress ring — animated stroke-dashoffset
const ProgressRing = ({ percent }) => {
  const size = 180;
  const strokeWidth = 10;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const safe = Math.max(0, Math.min(100, percent));
  const offset = circumference - (safe / 100) * circumference;

  return (
    <div className="relative inline-flex items-center justify-center" data-testid="progress-ring">
      <svg width={size} height={size} className="-rotate-90">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke="#2a2a2a"
          strokeWidth={strokeWidth}
          fill="none"
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke="#e8c547"
          strokeWidth={strokeWidth}
          fill="none"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          style={{ transition: 'stroke-dashoffset 600ms cubic-bezier(0.22, 1, 0.36, 1)' }}
        />
      </svg>
      {/* Spinning halo */}
      <svg
        width={size}
        height={size}
        className="absolute inset-0 animate-spin"
        style={{ animationDuration: '4s' }}
      >
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius + 8}
          stroke="#e8c547"
          strokeOpacity="0.12"
          strokeWidth="1"
          fill="none"
          strokeDasharray="2 14"
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span
          className="font-['DM_Serif_Display'] text-5xl text-[#e8c547] tabular-nums"
          data-testid="progress-percent"
        >
          {Math.round(safe)}
        </span>
        <span className="text-xs text-[#a0a0a0] tracking-widest mt-1">PERCENT</span>
      </div>
    </div>
  );
};

const ScriptUploadPage = () => {
  const { projectId } = useParams();
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const forceNew = searchParams.get('new') === '1';
  const [uploading, setUploading] = useState(false);
  const [uploadStarted, setUploadStarted] = useState(false);
  const [scriptStatus, setScriptStatus] = useState(null);
  const [checkingStatus, setCheckingStatus] = useState(true);
  const [displayPercent, setDisplayPercent] = useState(0);
  const [versionLabel, setVersionLabel] = useState('v1.0');
  // Language choice for non-English scripts (shown after detection)
  const [selectedLang, setSelectedLang] = useState('english');
  const [confirmingLang, setConfirmingLang] = useState(false);
  // Pre-flight cost gate (admins only): hold the selected file + uploaded script_id until user confirms
  const [pendingFile, setPendingFile] = useState(null);
  const [pendingScriptId, setPendingScriptId] = useState(null);
  const targetPercentRef = useRef(0);
  const isAdmin = user?.role === 'ADMIN';

  useEffect(() => {
    checkScriptStatus();
    const interval = setInterval(checkScriptStatus, 1500);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // While the upload HTTP request is in flight, force the ring visible immediately
  // with a tiny "Uploading & extracting text" stage so the user never sees a dead screen
  // even for large PDFs that take many seconds to parse server-side.
  useEffect(() => {
    if (uploadStarted) {
      targetPercentRef.current = Math.max(targetPercentRef.current, 2);
    }
  }, [uploadStarted]);

  // Smooth creep: bring displayPercent toward target every 250ms.
  // Between backend checkpoints, creep slowly toward (target + 6%) capped at 95%
  // so the bar never looks frozen during the long Claude call.
  useEffect(() => {
    const tick = setInterval(() => {
      setDisplayPercent((current) => {
        const backend = targetPercentRef.current;
        if (backend >= 100) return 100;
        // Allow creep up to 4% past backend value, capped at 95%
        const ceiling = Math.min(95, backend + 4);
        if (current < backend) {
          // Jump quickly toward backend value
          return Math.min(backend, current + Math.max(1, (backend - current) / 3));
        }
        if (current < ceiling) {
          return Math.min(ceiling, current + 0.25);
        }
        return current;
      });
    }, 250);
    return () => clearInterval(tick);
  }, []);

  const checkScriptStatus = async () => {
    try {
      const response = await axios.get(`${API}/projects/${projectId}/script-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setScriptStatus(response.data);

      const pct = response.data.progress_percent ?? (response.data.status === 'complete' ? 100 : 0);
      targetPercentRef.current = pct;
      if (response.data.status === 'complete') {
        setDisplayPercent(100);
      }

      // When the user explicitly clicked "Upload New Script", do NOT redirect on previously-complete script.
      if (response.data.status === 'complete' && !forceNew && !uploadStarted) {
        setTimeout(() => {
          navigate(`/projects/${projectId}/breakdown`);
        }, 2000);
      } else if (response.data.status === 'complete' && uploadStarted) {
        setTimeout(() => {
          navigate(`/projects/${projectId}/breakdown`);
        }, 2000);
      }
    } catch (error) {
      console.error('Error checking script status:', error);
    } finally {
      setCheckingStatus(false);
    }
  };

  const onDrop = useCallback(async (acceptedFiles) => {
    const file = acceptedFiles[0];
    if (!file) return;

    const allowedTypes = ['.pdf', '.fdx', '.fountain', '.txt'];
    const fileExt = '.' + file.name.split('.').pop().toLowerCase();
    if (!allowedTypes.includes(fileExt)) {
      toast.error('Unsupported file format. Please upload PDF, FDX, Fountain, or TXT.');
      return;
    }

    // ─── Admin + PDF → split flow with cost gate ──────────────────────────
    if (isAdmin && fileExt === '.pdf') {
      setUploading(true);
      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('version_label', versionLabel || '');
        const res = await axios.post(
          `${API}/projects/${projectId}/upload-pdf`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        setPendingScriptId(res.data.script_id);
        setPendingFile(file);
        toast.success(`PDF stored (${res.data.page_count} pages). Review cost before parsing.`);
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Upload failed');
      } finally {
        setUploading(false);
      }
      return;
    }

    // ─── Default (non-admin OR non-PDF) → legacy combined endpoint ────────
    setUploading(true);
    setUploadStarted(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('breakdown_language', 'auto');
      formData.append('version_label', versionLabel || '');

      await axios.post(`${API}/projects/${projectId}/upload-script`, formData, {
        headers: { Authorization: `Bearer ${token}` },
        onUploadProgress: (ev) => {
          if (ev.total) {
            const pct = Math.round((ev.loaded / ev.total) * 4);
            targetPercentRef.current = Math.max(targetPercentRef.current, pct);
          }
        },
      });

      toast.success('Script uploaded! AI is analyzing…');
      checkScriptStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload script');
      setUploadStarted(false);
    } finally {
      setUploading(false);
    }
  }, [projectId, token, versionLabel, isAdmin]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleConfirmParse = async () => {
    if (!pendingScriptId) return;
    try {
      setUploadStarted(true);
      targetPercentRef.current = 3;
      setDisplayPercent(3);
      await axios.post(
        `${API}/projects/${projectId}/confirm-parse`,
        { script_id: pendingScriptId, breakdown_language: 'auto' },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success('Parse confirmed. AI is analyzing…');
      setPendingFile(null);
      setPendingScriptId(null);
      checkScriptStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Confirm-parse failed');
      setUploadStarted(false);
    }
  };

  // Auto-proceed: once a file is staged AND the backend has returned a
  // pendingScriptId, confirm the parse immediately (no human gate).
  useEffect(() => {
    if (pendingFile && pendingScriptId) {
      handleConfirmParse();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingFile, pendingScriptId]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'text/plain': ['.txt', '.fountain'],
      'application/xml': ['.fdx']
    },
    maxFiles: 1,
    disabled: uploading
  });

  const handleUseSampleScript = async () => {
    setUploading(true);
    setUploadStarted(true);
    try {
      await axios.post(`${API}/projects/${projectId}/use-sample-script`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Sample script loaded! AI is analyzing...');
      checkScriptStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load sample script');
      setUploadStarted(false);
    } finally {
      setUploading(false);
    }
  };

  const reanalyzeScript = async () => {
    try {
      setUploadStarted(true);
      targetPercentRef.current = 3;
      setDisplayPercent(3);
      await axios.post(`${API}/projects/${projectId}/reanalyze-script`,
        { breakdown_language: scriptStatus?.breakdown_language || 'english' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Re-analysis started! AI is re-processing your full script…');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start re-analysis');
      setUploadStarted(false);
    }
  };

  const confirmLanguageChoice = async () => {
    setConfirmingLang(true);
    try {
      targetPercentRef.current = 3;
      setDisplayPercent(3);
      await axios.post(`${API}/projects/${projectId}/reanalyze-script`,
        { breakdown_language: selectedLang },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setUploadStarted(true);
      toast.success(`Starting breakdown in ${selectedLang === 'malayalam' ? 'Malayalam' : selectedLang === 'mixed' ? 'Mixed' : 'English'}…`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start analysis');
    } finally {
      setConfirmingLang(false);
    }
  };

  if (checkingStatus) {
    return (
      <div className="min-h-screen bg-[#0d0d0d] flex items-center justify-center">
        <div className="text-[#a0a0a0]">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0d0d0d] p-8" data-testid="script-upload-page">
      <div className="max-w-4xl mx-auto">
        <div className="mb-8">
          <Button
            variant="ghost"
            onClick={() => navigate('/dashboard')}
            className="text-[#a0a0a0] hover:text-[#f0f0f0] mb-4"
            data-testid="back-to-dashboard-btn"
          >
            ← Back to Dashboard
          </Button>
          <h1 className="font-['DM_Serif_Display'] text-4xl text-[#f0f0f0] mb-2">Upload Script</h1>
          <p className="text-[#a0a0a0]">Upload your screenplay and let AI break it down</p>
        </div>

        {/* Hide upload UI once the user has started uploading — show the ring instead */}
        {(scriptStatus?.status === 'no_script' || forceNew) && !uploadStarted && (
          <div className="space-y-4">

            {/* Script Version label */}
            <Card className="bg-[#1a1a1a] border-[#2a2a2a] p-5" data-testid="version-label-card">
              <div className="flex items-center gap-2 mb-3">
                <Sparkles className="h-4 w-4 text-[#c084fc]" />
                <span className="text-sm font-medium text-[#f0f0f0]">Script Version</span>
                <span className="text-xs text-[#505050] ml-1">— e.g. v1.0, draft 3, shooting draft</span>
              </div>
              <input
                type="text"
                value={versionLabel}
                onChange={e => setVersionLabel(e.target.value)}
                placeholder="v1.0"
                maxLength={40}
                data-testid="version-label-input"
                className="w-full max-w-sm bg-[#0d0d0d] border border-[#2a2a2a] rounded-md text-sm text-[#f0f0f0] px-3 py-2 focus:outline-none focus:border-[#e8c547]/40"
              />
            </Card>

            <Card className="bg-[#1a1a1a] border-[#2a2a2a] p-12" data-testid="upload-card">
              {pendingFile ? (
                <div className="flex flex-col items-center justify-center py-8 text-center" data-testid="upload-processing">
                  <div className="h-10 w-10 rounded-full border-2 border-[#e8c547]/30 border-t-[#e8c547] animate-spin mb-4" />
                  <p className="text-[#a0a0a0] text-sm">Processing…</p>
                </div>
              ) : (
                <div
                  {...getRootProps()}
                  className={`border-2 border-dashed rounded-lg p-12 text-center cursor-pointer transition-all duration-200 ${
                    isDragActive
                      ? 'border-[#e8c547] bg-[#e8c547]/5'
                      : 'border-[#2a2a2a] hover:border-[#e8c547]'
                  }`}
                >
                <input {...getInputProps()} data-testid="file-input" />
                {uploading ? (
                  <div className="flex flex-col items-center">
                    <Loader2 className="h-16 w-16 text-[#e8c547] animate-spin mb-4" />
                    <p className="text-[#f0f0f0] text-lg">Uploading...</p>
                  </div>
                ) : (
                  <div className="flex flex-col items-center">
                    <Upload className="h-16 w-16 text-[#a0a0a0] mb-4" />
                    <p className="text-[#f0f0f0] text-lg mb-2">
                      {isDragActive ? 'Drop the script here' : 'Drag & drop your script here'}
                    </p>
                    <p className="text-[#a0a0a0] text-sm mb-4">or click to browse</p>
                    <div className="flex flex-wrap gap-2 justify-center">
                      <span className="text-xs text-[#a0a0a0] bg-[#0d0d0d] px-3 py-1 rounded">PDF</span>
                      <span className="text-xs text-[#a0a0a0] bg-[#0d0d0d] px-3 py-1 rounded">FDX</span>
                      <span className="text-xs text-[#a0a0a0] bg-[#0d0d0d] px-3 py-1 rounded">Fountain</span>
                      <span className="text-xs text-[#a0a0a0] bg-[#0d0d0d] px-3 py-1 rounded">TXT</span>
                    </div>
                    <p className="text-xs text-[#a0a0a0] mt-3">Max file size: 50MB</p>
                  </div>
                )}
                </div>
              )}
            </Card>

            {/* Divider */}
            <div className="flex items-center gap-4">
              <div className="flex-1 h-px bg-[#2a2a2a]" />
              <span className="text-[#a0a0a0] text-sm">OR</span>
              <div className="flex-1 h-px bg-[#2a2a2a]" />
            </div>

            {/* Sample Script Option */}
            <Card className="bg-[#1a1a1a] border-[#2a2a2a] p-6" data-testid="sample-script-card">
              <div className="flex items-center justify-between gap-6">
                <div className="flex items-start gap-4 flex-1">
                  <div className="h-12 w-12 rounded-lg bg-[#4a9eff]/10 flex items-center justify-center flex-shrink-0">
                    <Sparkles className="h-6 w-6 text-[#4a9eff]" strokeWidth={1.5} />
                  </div>
                  <div>
                    <h3 className="font-['DM_Serif_Display'] text-xl text-[#f0f0f0] mb-1">Try with Sample Script</h3>
                    <p className="text-[#a0a0a0] text-sm">
                      Don't have a script ready? Use our detective thriller sample to see FiPr.ai in action.
                    </p>
                  </div>
                </div>
                <Button
                  onClick={handleUseSampleScript}
                  disabled={uploading}
                  variant="outline"
                  className="border-[#4a9eff] text-[#4a9eff] hover:bg-[#4a9eff]/10 flex-shrink-0"
                  data-testid="use-sample-script-btn"
                >
                  {uploading ? 'Loading...' : 'Use Sample'}
                </Button>
              </div>
            </Card>
          </div>
        )}

        {/* Language Choice Dialog — shown when non-English script is detected */}
        {scriptStatus?.status === 'awaiting_language' && (
          <Card className="bg-[#1a1a1a] border-[#e8c547]/30 p-8 sm:p-12" data-testid="language-choice-card">
            <div className="flex flex-col items-center text-center mb-8">
              <div className="h-16 w-16 rounded-full bg-[#e8c547]/10 flex items-center justify-center mb-4">
                <Languages className="h-8 w-8 text-[#e8c547]" />
              </div>
              <h3 className="font-['DM_Serif_Display'] text-2xl text-[#f0f0f0] mb-2">
                {scriptStatus.detected_language_display || 'Non-English'} Script Detected
              </h3>
              <p className="text-[#a0a0a0] text-sm max-w-md">
                Your script is in <span className="text-[#e8c547] font-semibold">{scriptStatus.detected_language_display}</span>.
                How would you like the breakdown to be generated?
                Scene numbers and INT/EXT markers will always be in English.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
              {[
                {
                  value: 'english',
                  label: 'English',
                  icon: '🇬🇧',
                  desc: 'Full breakdown in English — synopses, character names, and all notes translated to English.',
                },
                {
                  value: scriptStatus.detected_language === 'ml' ? 'malayalam' : 'original',
                  label: scriptStatus.detected_language_display || 'Original',
                  icon: '🌐',
                  desc: `Synopsis and actions in ${scriptStatus.detected_language_display}. Scene numbers and INT/EXT always in English. Character names in English.`,
                },
                {
                  value: 'mixed',
                  label: 'Mixed',
                  icon: '🔄',
                  desc: `1-line synopsis in ${scriptStatus.detected_language_display}, all breakdown details (props, costumes, VFX) in English.`,
                },
              ].map(opt => (
                <button
                  key={opt.value}
                  data-testid={`lang-choice-${opt.value}`}
                  onClick={() => setSelectedLang(opt.value)}
                  className={`text-left rounded-xl border p-4 transition-all duration-150 ${
                    selectedLang === opt.value
                      ? 'border-[#e8c547] bg-[#e8c547]/8 shadow-[0_0_0_1px_#e8c547]'
                      : 'border-[#2a2a2a] hover:border-[#3a3a3a] bg-[#111]'
                  }`}
                >
                  <div className="text-2xl mb-2">{opt.icon}</div>
                  <div className={`text-sm font-semibold mb-1.5 ${selectedLang === opt.value ? 'text-[#e8c547]' : 'text-[#f0f0f0]'}`}>
                    {opt.label}
                  </div>
                  <div className="text-xs text-[#606060] leading-relaxed">{opt.desc}</div>
                  {selectedLang === opt.value && (
                    <div className="mt-2 flex items-center gap-1 text-[#e8c547] text-xs font-semibold">
                      <CheckCircle className="h-3 w-3" /> Selected
                    </div>
                  )}
                </button>
              ))}
            </div>

            <div className="flex justify-center">
              <Button
                onClick={confirmLanguageChoice}
                disabled={confirmingLang}
                className="bg-[#e8c547] hover:bg-[#d6b539] text-black font-semibold px-8 py-3 h-auto text-sm"
                data-testid="confirm-language-btn"
              >
                {confirmingLang ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Starting Analysis…</>
                ) : (
                  <><ArrowRight className="h-4 w-4 mr-2" /> Generate Breakdown in {selectedLang === 'english' ? 'English' : selectedLang === 'mixed' ? 'Mixed Mode' : (scriptStatus.detected_language_display || 'Original Language')}</>
                )}
              </Button>
            </div>
          </Card>
        )}

        {/* ── Worker-restart failure: surface clean retry instead of generic error ── */}
        {scriptStatus?.status === 'failed' && scriptStatus?.processing_error === 'worker_restart' && (
          <Card className="bg-[#1a1a1a] border-[#7a4a1a] p-10 text-center" data-testid="worker-restart-card">
            <h3 className="font-['DM_Serif_Display'] text-2xl text-[#f0f0f0] mb-3">
              Analysis was interrupted by a server restart
            </h3>
            <p className="text-[#a0a0a0] text-sm mb-2">
              Your script upload was saved, but the AI analysis didn't finish because the server restarted mid-run.
            </p>
            <p className="text-[#707070] text-xs mb-6">
              Click below to pick up where it left off — no re-upload needed.
            </p>
            {scriptStatus?.filename && (
              <div className="flex items-center justify-center gap-2 text-sm text-[#a0a0a0] mb-6">
                <File className="h-4 w-4" />
                <span>{scriptStatus.filename}</span>
              </div>
            )}
            <Button
              onClick={reanalyzeScript}
              className="bg-[#e8c547] hover:bg-[#d4b139] text-[#0a0a0a] font-semibold px-6 h-10"
              data-testid="worker-restart-retry-btn"
            >
              Retry Analysis
            </Button>
          </Card>
        )}

        {(scriptStatus?.status === 'processing' || (uploadStarted && scriptStatus?.status !== 'complete' && scriptStatus?.status !== 'error' && scriptStatus?.status !== 'awaiting_language' && scriptStatus?.status !== 'failed')) && (
          <Card className="bg-[#1a1a1a] border-[#2a2a2a] p-12 text-center" data-testid="processing-card">
            <div className="flex justify-center mb-6">
              <ProgressRing percent={displayPercent} />
            </div>
            <h3 className="font-['DM_Serif_Display'] text-2xl text-[#f0f0f0] mb-2">
              {scriptStatus?.status === 'processing' ? 'AI is analyzing your script…' : 'Uploading your script…'}
            </h3>
            <p
              className="text-[#e8c547] text-sm font-medium tracking-wide mb-1 min-h-[1.25rem]"
              data-testid="progress-stage"
            >
              {scriptStatus?.progress_stage || (uploadStarted ? 'Uploading & extracting text' : 'Initializing')}
            </p>
            <p className="text-[#a0a0a0] text-sm mb-6">This usually takes 2–8 minutes depending on script length</p>
            {scriptStatus?.filename && (
              <div className="flex items-center justify-center gap-2 text-sm text-[#a0a0a0]">
                <File className="h-4 w-4" />
                <span>{scriptStatus.filename}</span>
              </div>
            )}
            <div className="mt-6 space-y-2 text-left max-w-md mx-auto">
              {[
                { label: 'Reading script', threshold: 8 },
                { label: 'Detecting scenes & characters', threshold: 15 },
                { label: 'Extracting props, costumes & VFX', threshold: 70 },
                { label: 'Building breakdown sheets', threshold: 92 },
              ].map((step) => {
                const done = displayPercent >= step.threshold + 8;
                const active = displayPercent >= step.threshold && !done;
                return (
                  <div
                    key={step.label}
                    className={`flex items-center gap-2 text-sm transition-colors duration-300 ${
                      done ? 'text-[#44bb88]' : active ? 'text-[#f0f0f0]' : 'text-[#5a5a5a]'
                    }`}
                  >
                    {done ? (
                      <CheckCircle className="h-4 w-4 flex-shrink-0" />
                    ) : (
                      <div
                        className={`h-2 w-2 rounded-full flex-shrink-0 ${
                          active ? 'bg-[#e8c547] animate-pulse' : 'bg-[#2a2a2a]'
                        }`}
                      />
                    )}
                    {step.label}
                  </div>
                );
              })}
            </div>
          </Card>
        )}

        {scriptStatus?.status === 'complete' && (
          <Card className="bg-[#1a1a1a] border-[#2a2a2a] p-12 text-center" data-testid="complete-card">
            <CheckCircle className="h-16 w-16 text-[#44bb88] mx-auto mb-4" />
            <h3 className="font-['DM_Serif_Display'] text-2xl text-[#f0f0f0] mb-2">Breakdown Complete!</h3>
            <p className="text-[#a0a0a0] mb-4">Your script has been analyzed and organized by department</p>

            {/* Extraction method info */}
            {scriptStatus?.extraction_method && (() => {
              const EXTRACT_META = {
                pypdf2_direct:      { label: 'Standard Text',            color: '#4ade80', icon: '✓', desc: 'Standard PDF with embedded text.' },
                pypdf2_indic_clean: { label: 'Multilingual Text',        color: '#60a5fa', icon: '✓', desc: 'Indic-script PDF with clean embedded text.' },
                pypdf2_cid_corrupt: { label: 'AI Vision Read',           color: '#f59e0b', icon: '⚠', desc: 'Non-standard PDF fonts detected. AI vision reads scene text from rendered pages.' },
                pypdf2_failed:      { label: 'Scanned PDF — Needs review', color: '#f87171', icon: '✗', desc: 'Very little text extracted. This PDF may be image-based.' },
                plain_text:         { label: 'Plain Text File',            color: '#a78bfa', icon: '✓', desc: 'Non-PDF: text read directly.' },
              };
              const m = EXTRACT_META[scriptStatus.extraction_method];
              if (!m) return null;
              return (
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-lg mb-6 mx-auto"
                     style={{ background: `${m.color}12`, border: `1px solid ${m.color}30` }}
                     data-testid="extraction-method-complete">
                  <span className="text-sm" style={{ color: m.color }}>{m.icon}</span>
                  <div className="text-left">
                    <div className="text-xs font-semibold" style={{ color: m.color }}>
                      Extraction Method: {m.label}
                    </div>
                    <div className="text-[11px]" style={{ color: '#606060' }}>{m.desc}</div>
                  </div>
                </div>
              );
            })()}

            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button
                onClick={() => navigate(`/projects/${projectId}/breakdown`)}
                className="bg-[#e8c547] hover:bg-[#d6b539] text-black font-medium"
                data-testid="view-breakdown-btn"
              >
                View Master Breakdown
              </Button>
              <Button
                variant="outline"
                onClick={reanalyzeScript}
                className="border-[#2a2a2a] text-[#a0a0a0] hover:text-[#f0f0f0]"
                data-testid="reanalyze-script-btn"
              >
                Re-analyze Script
              </Button>
            </div>
          </Card>
        )}

        {scriptStatus?.status === 'error' && (
          <Card
            className="bg-[#1a1a1a] p-8 sm:p-12 text-center border border-[#ff4444]/30"
            data-testid="error-card"
          >
            <AlertCircle className="h-16 w-16 text-[#ff4444] mx-auto mb-4" />

            <h3 className="font-['DM_Serif_Display'] text-2xl text-[#f0f0f0] mb-2">
              Processing Failed
            </h3>

            <div className="max-w-md mx-auto">
              <p className="text-[#a0a0a0] mb-2">There was an error analyzing your script.</p>
              {scriptStatus.progress_stage && (
                <div className="bg-[#0d0d0d] border border-[#ff4444]/30 rounded-lg p-4 mt-3 mb-5 text-left" data-testid="error-reason">
                  <p className="text-xs text-[#ff8888] font-mono uppercase tracking-wider mb-1">Reason</p>
                  <p className="text-sm text-[#f0f0f0] break-words">{scriptStatus.progress_stage.replace(/^Failed:\s*/, '')}</p>
                </div>
              )}
              <Button
                onClick={() => { setUploadStarted(false); setScriptStatus({ status: 'no_script' }); }}
                className="bg-[#e8c547] hover:bg-[#d6b539] text-black font-medium"
                data-testid="retry-upload-btn"
              >
                Try Again
              </Button>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
};

export default ScriptUploadPage;
